import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import {
  useUpgradePromptRules,
  WithEnterpriseManagedOverride,
} from 'app/src/components/UpgradePrompts';

import styles from './CreateBoardPopoverButton.less';

interface Props {
  workspaceId: string;
  workspaceName: string;
  analyticsSource: SourceType;
}

export const CreateBoardPopoverButton: FunctionComponent<Props> = ({
  workspaceId,
  workspaceName,
  analyticsSource,
}) => {
  const { isEligible } = useFreeTrialEligibilityRules(workspaceId, {
    skip: !workspaceId,
  });

  const { openPlanSelection } = useUpgradePromptRules(
    workspaceId,
    `${analyticsSource}-${workspaceId}`,
  );

  const onClick = useCallback(() => {
    openPlanSelection();
    Analytics.sendClickedLinkEvent({
      linkName: 'bcUpgradePrompt',
      source: analyticsSource,
      containers: {
        organization: {
          id: workspaceId,
        },
      },
      attributes: {
        isFreeTrial: isEligible,
        promptId: `${analyticsSource}Prompt`,
      },
    });
  }, [openPlanSelection, analyticsSource, workspaceId, isEligible]);

  <FormattedMessage
    id="upgrade-path-audit.cannot-copy-board-limit-reached"
    defaultMessage="Looks like this board can't be copied into {teamName} because this free Workspace has hit the maximum number of open boards. For unlimited Workspace boards (and more),"
    description="A message indicating this board cannot be copied because this free Worksapce has hit the maximum number of open boards"
    values={{
      teamName: <span key={'team-name'}>{workspaceName}</span>,
    }}
  />;
  return (
    <>
      <FormattedMessage
        id="upgrade-path-audit.free-workspace-can-only-have-10-open-boards"
        defaultMessage="Free Workspaces can only have 10 open boards. To add more boards, upgrade your Workspace."
        description="A message indicating that free Workspaces can only have 10 open boards"
        tagName="p"
      />
      <a href="#" onClick={onClick} className={styles.ctaButton}>
        <WithEnterpriseManagedOverride>
          <FormattedMessage
            id="upgrade-path-audit.upgrade-now"
            defaultMessage="Upgrade now"
            description="A call to action to upgrade this Workspace"
          />
        </WithEnterpriseManagedOverride>
      </a>
    </>
  );
};
